<template>
  <div class="max-w-7xl mx-auto p-8">
    <header class="text-center mb-8">
      <h1 class="text-display-xl mb-4">Impact of Disability Statement</h1>
      <p class="text-text-xl max-w-3xl mx-auto">
        Compose a statement about how an individual student's disability impacts their involvement and progress in the general education curriculum.
      </p>
    </header>
    <div v-if="!visibilityStore.iodVisibility" class="flex min-h-[400px] justify-center">
      <span>This page is not available.</span>
    </div>
    <main v-else-if="!loadingOptions">
      <ImpactOfDisabilityWizard
        v-if="!disability"
        :disability-options="disabilityOptions"
        :disability="disability"
        @update:disability="updateDisability"
        :subject-options="subjectOptions"
        :subjects="subjects"
        @update:subjects="updateSubjects"
        :impact-areas-by-disability="impactAreasByDisability"
        :impact-areas="impactAreas"
        @update:impact-areas="updateImpactAreas"
        :loading-options="loadingOptions"
      />

      <div class="grid grid-cols-12 gap-8" v-else>
        <div class="col-span-12 md:col-span-4 lg:col-span-3">
          <ImpactOfDisabilityFilters
            :disability-options="disabilityOptions"
            :disability="disability"
            @update:disability="updateDisability"
            :subject-options="subjectOptions"
            :subjects="subjects"
            @update:subjects="updateSubjects"
            :change-requires-confirmation="changeRequiresConfirmation"
            @confirmupdate:disability="(newVal) => confirmFilterChange(newVal, 'disability')"
            @confirmupdate:subjects="(newVal) => confirmFilterChange(newVal, 'subject')"
          />
        </div>
        <div class="col-span-12 md:col-span-8 lg:col-span-9">
          <ImpactOfDisabilityResults
            :disability="disability"
            :disability-label="disabilityLabel"
            :subjects="subjects"
            :impact-area-options="impactAreaOptions"
            :impact-areas="impactAreas"
            @update:impact-areas="updateImpactAreas"
            :change-requires-confirmation="changeRequiresConfirmation"
            @update:change-requires-confirmation="updateChangeRequiresConfirmation"
            :loading="loadingOptions"
            @confirmupdate:impact-areas="(newVal) => confirmFilterChange(newVal, 'impactArea')"
          />
        </div>
      </div>
    </main>
    <div v-else class="flex justify-center items-center min-h-[400px] flex-col">
      <ProgressSpinner class="h-4 w-4 stroke-primary-400" strokeWidth="4" />
      <span class="ml-2">Loading...</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ImpactOfDisabilityWizard from './ImpactOfDisabilityWizard.vue'
import ImpactOfDisabilityResults from './ImpactOfDisabilityResults.vue'
import ImpactOfDisabilityFilters from './ImpactOfDisabilityFilters.vue'
import ProgressSpinner from 'primevue/progressspinner'
import { getDisabilitiesAndImpactAreas } from './api'
import { getIODSubjects } from './subjects'
import { useUserStore } from '@/authentication/user.store'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { IMPACT_OF_DISABILITY_EVENT_NAMES } from './event_names'
import type { ImpactArea, DisabilityOption } from './impact-of-disability_types'
import { useVisibilityStore } from '@/visibility/visibility.store'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const contentProfileStore = useContentProfileStore()
const analytics = getAnalyticsTracker()
const visibilityStore = useVisibilityStore()

const disabilityOptions = ref<DisabilityOption[]>([])
const disability = ref('')
const subjects = ref<string[]>([])
const impactAreasByDisability = ref<Map<string, ImpactArea[]>>(new Map())
const impactAreas = ref<ImpactArea[]>([])
const loadingOptions = ref(false)
const changeRequiresConfirmation = ref(false)

const impactAreaOptions = computed(() => {
  if (disability.value) {
    const areas = impactAreasByDisability.value.get(disability.value) || []
    return areas.sort((a, b) => parseInt(a.area_order) - parseInt(b.area_order))
  }
  return []
})

const subjectOptions = computed(() => getIODSubjects())

const disabilityLabel = computed(() => {
  if (disability.value) {
    const option = disabilityOptions.value.find(elem => elem.value === disability.value)
    return option?.label || ''
  }
  return ''
})

const isPremium = computed(() => userStore.isPremium)

onMounted(async () => {
  if (visibilityStore.iodVisibility) {
    await getAndSetDisabilitiesAndImpactAreas()
    
    const disabilityFromUrl = (route.query.disability as string) || ''
    
    if (!disabilityOptions.value.some(option => option.value === disabilityFromUrl && !option.locked)) {
      disability.value = ''
    } else {
      disability.value = disabilityFromUrl
      setSubjectsAndImpactAreasFromUrl()
    }

    if (!disability.value) {
      analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.VIEWED_WIZARD)
    }
  }
})

watch(() => contentProfileStore.profileId, async () => {
  router.replace({ name: 'ImpactOfDisability' })
})

watch(disability, (newVal, oldVal) => {
  if (oldVal !== '' && oldVal !== newVal) {
    impactAreas.value = []
  }
  if (oldVal !== '' && newVal !== '') {
    analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.CHANGED_DISABILITY)
  }
})

watch(subjects, (newVal, oldVal) => {
  if (newVal.length > oldVal.length) {
    const addedVal = newVal.find(subj => !oldVal.includes(subj))
    if (addedVal) {
      analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.SELECTED_SUBJECT, { iod_selected_subject: addedVal })
    }
  } else {
    const removedVal = oldVal.find(subj => !newVal.includes(subj))
    if (removedVal) {
      analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.REMOVED_SUBJECT, { iod_removed_subject: removedVal })
    }
  }
})

watch(impactAreas, (newVal, oldVal) => {
  if (newVal.length > oldVal.length) {
    const addedVal = newVal.find(subj => oldVal.every(oldSubj => subj.area_uuid !== oldSubj.area_uuid))
    if (addedVal) {
      analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.SELECTED_IMPACT_AREA, { iod_selected_impact_area: addedVal.area_label })
    }
  } else if (oldVal.length) {
    const removedVal = oldVal.find(subj => newVal.every(newSubj => subj.area_uuid !== newSubj.area_uuid))
    if (removedVal) {
      analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.REMOVED_IMPACT_AREA, { iod_removed_impact_area: removedVal.area_label })
    }
  }
})

watch(() => route.query.disability, (to) => {
  if (!to) {
    // Reset all state when navigating without query params (e.g., from nav menu)
    disability.value = ''
    subjects.value = []
    impactAreas.value = []
  } else if (to && disabilityOptions.value.some(option => option.value === to && !option.locked)) {
    // When disability is added to URL (e.g., from Compose button), update parent state
    disability.value = to as string
    setSubjectsAndImpactAreasFromUrl()
  }
})

async function getAndSetDisabilitiesAndImpactAreas() {
  loadingOptions.value = true
  
  try {
    const disabilitiesAndImpactAreas = await getDisabilitiesAndImpactAreas(
      userStore.licenseSubType,
      contentProfileStore.profileId
    )
    
    if (disabilitiesAndImpactAreas.disabilities) {
      disabilityOptions.value = disabilitiesAndImpactAreas.disabilities.map(item => ({
        label: item.disability,
        value: item.disability_uuid,
        locked: isDisabilityLocked(item)
      }))
      
      impactAreasByDisability.value = new Map(
        disabilitiesAndImpactAreas.disabilities.map(item => [item.disability_uuid, item.areas_of_impact])
      )
    }
  } catch (error) {
    console.error('Failed to load disabilities and impact areas:', error)
  } finally {
    loadingOptions.value = false
  }
}

function setSubjectsAndImpactAreasFromUrl() {
  if (route.query.subjects) {
    let subjectsFromUrl: string[]
    if (Array.isArray(route.query.subjects)) {
      subjectsFromUrl = route.query.subjects as string[]
    } else {
      subjectsFromUrl = [route.query.subjects as string]
    }
    subjects.value = subjectsFromUrl.filter(subject => 
      subjectOptions.value.some(option => option.value === subject)
    )
  } else {
    subjects.value = []
  }

  if (route.query.impactAreas) {
    if (Array.isArray(route.query.impactAreas)) {
      impactAreas.value = impactAreaOptions.value.filter(ia => 
        (route.query.impactAreas as string[]).includes(ia.area_uuid)
      )
    } else {
      impactAreas.value = impactAreaOptions.value.filter(ia => 
        ia.area_uuid === route.query.impactAreas
      )
    }
  } else {
    impactAreas.value = []
  }
}

async function confirmFilterChange(newVal: any, filterType: string) {
  const confirmed = window.confirm(
    'If you make this selection, your edits will be lost.\n\nDo you want to navigate away from your edited statement?'
  )
  
  if (confirmed) {
    switch (filterType) {
      case 'disability':
        setNewDisability(newVal)
        break
      case 'subject':
        setNewSubject(newVal)
        break
      case 'impactArea':
        setNewImpactArea(newVal)
        break
    }
    analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.RESET_STATEMENT_TEXT)
  } else {
    analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.DISMISSED_STATEMENT_RESET)
  }
}

function setNewDisability(newVal: string) {
  disability.value = newVal
}

function setNewSubject(newVal: string) {
  let newSubjects = [...subjects.value]
  if (subjects.value.includes(newVal)) {
    newSubjects = newSubjects.filter(subj => subj !== newVal)
  } else {
    newSubjects.push(newVal)
  }
  subjects.value = newSubjects
}

function setNewImpactArea(newVal: ImpactArea[]) {
  impactAreas.value = newVal
}

function isDisabilityLocked(disabilityObj: any) {
  if (isPremium.value) {
    return false
  }
  return disabilityObj.areas_of_impact.every((ia: ImpactArea) => ia.trial_access === 'FALSE')
}

function updateDisability(newVal: string) {
  disability.value = newVal
}

function updateSubjects(newVal: string[]) {
  subjects.value = newVal
}

function updateImpactAreas(newVal: ImpactArea[]) {
  impactAreas.value = newVal
}

function updateChangeRequiresConfirmation(newVal: boolean) {
  changeRequiresConfirmation.value = newVal
}
</script>
